import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import Tabs from 'app/components/Tabs';
import TeamAvatar from 'app/components/TeamAvatar';
import StatValue from 'app/components/StatValue';
import { round } from 'app/utils/number';
import { localizedHeroName } from 'app/i18n/utils';
import Tooltip from 'app/components/Tooltip';
import { cssVariable } from 'app/utils/style';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

interface Props {
  data: TeamsStatsBlockMatch;
}

const HeroesPool: FC<Props> = ({ data }) => {
  const [selectedTab, setSelectedTab] = useState<'most_picked' | 'most_banned'>('most_picked');
  const { t } = useTranslation();

  const opponent1Stat = data.opponents.at(0)?.stats as StatsDota2 | undefined;
  const opponent2Stat = data.opponents.at(1)?.stats as StatsDota2 | undefined;

  const opponentsData: Record<typeof selectedTab, HeroStat[][]> = {
    most_banned: [
      opponent1Stat?.most_banned_against ?? [],
      opponent2Stat?.most_banned_against ?? []
    ],
    most_picked: [opponent1Stat?.most_picked ?? [], opponent2Stat?.most_picked ?? []]
  };

  const hasMostPicked = opponentsData.most_picked.some(opponentStat => opponentStat.length > 0);

  const hasMostBanned = opponentsData.most_banned.some(opponentStat => opponentStat.length > 0);

  const tabs: (typeof selectedTab)[] = [
    hasMostPicked && 'most_picked',
    hasMostBanned && 'most_banned'
  ].filter(Boolean) as (typeof selectedTab)[];

  const displayedData = opponentsData[selectedTab];

  const nbOfHeroesDisplayed = Math.max(...displayedData.map(opponentStat => opponentStat.length));

  return (
    <BlockGradTemplate hasData={hasMostPicked || hasMostBanned} title={t('block-pool-hero-title')}>
      <Tabs
        tabs={tabs}
        selectedTab={selectedTab}
        selectTab={setSelectedTab}
        compact
        translationKey="block-pool-hero-tabs"
      />
      <div className={style.teamHeroPoolContainer}>
        {displayedData.map((opponentStat, index) => (
          <div key={index} className={index === 0 ? style.teamHeroPool1 : style.teamHeroPool2}>
            <TeamAvatar team={data.opponents.at(index)} className={style.teamLogo} />
            <div className={style.heroPool}>
              {new Array(nbOfHeroesDisplayed).fill(null).map((_, heroIndex) => {
                const hero = opponentStat.at(heroIndex);
                return (
                  <div key={`team${index}_hero_${hero?.id ?? heroIndex}`} className={style.hero}>
                    {hero ? (
                      <Tooltip
                        content={localizedHeroName(hero, 'dota2', t)}
                        className={style.heroTooltip}
                      >
                        <span
                          style={cssVariable('hero', `url(${getCDNUrl(hero.image_url)})`)}
                          className={style.heroImage}
                        />
                      </Tooltip>
                    ) : (
                      <span className={style.heroPlaceholder} />
                    )}
                    <StatValue
                      value={hero?.presence_percentage}
                      displayFunction={value => `${round(value * 100, 0)}%`}
                      greyedOutIfNull={false}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </BlockGradTemplate>
  );
};

HeroesPool.displayName = 'HeroesPool';

export default HeroesPool;
